/** JSON stored in `order_items.order_line` — custom charge rows (array); legacy single object supported when reading. */
export interface OrderItemOrderLine {
  type: string;
  name: string;
  quantity: number;
  /** When `is_fixed` is true: unit price × quantity (may be negative for credits). */
  price: number;
  /**
   * When `is_fixed` is false: percent of factory-product subtotal (may be negative for credits).
   */
  percentage?: number;
  /** `true` = fixed unit price × qty; `false` = percentage of products subtotal (normalized on save/sync). */
  is_fixed?: boolean;
  /** Computed line amount; updated when totals are recalculated in the price breakdown modal. */
  total_price?: number;
}

/**
 * Fixed mode: unit price × quantity. Percentage mode: `percentage` of product subtotal.
 * Explicit `is_fixed` from API wins; if missing, non-zero `percentage` implies percentage mode.
 */
export function orderLineUsesFixedPricing(line: any): boolean {
  if (!line || line.type !== 'add_order_item_line') return true;
  if (line.is_fixed === false || line.is_fixed === 0 || line.is_fixed === '0') return false;
  if (line.is_fixed === true || line.is_fixed === 1 || line.is_fixed === '1') return true;
  const pct = parseFloat(String(line.percentage ?? '').replace(',', '.'));
  if (Number.isFinite(pct) && pct !== 0) return false;
  return true;
}

/**
 * Normalize DB/API `order_line` to a list of valid line objects (mutable refs preserved when already an array).
 * Legacy: one object `{ type: 'add_order_item_line', ... }` becomes a one-element array (new array, same object ref).
 */
export function normalizeOrderLinesFromApi(raw: unknown): OrderItemOrderLine[] {
  if (raw == null) return [];
  if (Array.isArray(raw)) {
    return raw.filter((x: any) => x && x.type === 'add_order_item_line') as OrderItemOrderLine[];
  }
  if (typeof raw === 'object' && (raw as OrderItemOrderLine).type === 'add_order_item_line') {
    return [raw as OrderItemOrderLine];
  }
  return [];
}
